<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'UcContractServices',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    contract: {
      type: Object,
      required: true,
    },
    readonly: {
      type: Boolean,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const filterItems = (...args) => {
    return _.sortBy(args[0], (p) => (isServiceStale(p) ? '_' + p.name : p.id));
  };
  const isServiceStale = (...args) => {
    const [service] = args;
    return (
      service.valid_to && $fn.value.parseISODate(service.valid_to) < today.value
    );
  };

  const today = ref(null);
  const displayHelper = ref(null);
  const ux = ref([]);

  const $data = computed(() => ({
    today: today.value,
    displayHelper: displayHelper.value,
    ux: ux.value,
  }));

  const $$load_today = async () => {
    return new Date();
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [{ name: 'today' }, { name: 'displayHelper' }, { name: 'ux' }],
    loaders: { today: $$load_today },
    ux: {},
    dataRefs: { today, displayHelper, ux },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  watch(
    () => props.contract,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('contract');
    }
  );
  watch(
    () => props.readonly,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('readonly');
    }
  );

  const act_2095089031469157_emit_qf5h3wwge_getActionArgs = async ($event) => {
    return {
      event: 'reload',
    };
  };
  const inst_evh_2095089031469157_close_8sz4kef9g_getWhen = async ($event) => {
    return !!$event.data.modalResult.ok;
  };
  const act_2095089031469157_showModal_fa1vdg163_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'ContractServiceEditor',
      props: {
        mode: 'addService',
        contract: props.contract,
      },
    };
  };
  const act_294572930540096_emit_0sb9uvzxx_getActionArgs = async ($event) => {
    return {
      event: 'reload',
    };
  };
  const inst_evh_294572930540096_close_sq5j61vab_getWhen = async ($event) => {
    return !!$event.data.modalResult.ok;
  };
  const act_294572930540096_showModal_kk2l5thy1_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'ContractServiceEditor',
      props: {
        mode: 'editService',
        contract: props.contract,
        service: _.cloneDeep($event.scope.service),
      },
    };
  };
  const act_6027561141562583_emit_4oncvilsr_getActionArgs = async ($event) => {
    return {
      event: 'reload',
    };
  };

  const act_7315092382398562_reloadComponentData_nt3w0ctlo =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_meibz6qtv =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_nt3w0ctlo,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_meibz6qtv],
    event: 'reload',
  });
  const act_2095089031469157_emit_qf5h3wwge = new actionTypes.EmitAction({
    actionArgs: act_2095089031469157_emit_qf5h3wwge_getActionArgs,
    displayName: 'emitReload',
  });
  const inst_evh_2095089031469157_close_8sz4kef9g =
    new actionTypes.EventHandlerImpl({
      action: act_2095089031469157_emit_qf5h3wwge,
      event: 'close',
      when: inst_evh_2095089031469157_close_8sz4kef9g_getWhen,
      displayName: 'emitReload',
    });
  const act_2095089031469157_showModal_fa1vdg163 =
    new actionTypes.ShowModalAction({
      actionArgs: act_2095089031469157_showModal_fa1vdg163_getActionArgs,
      events: [inst_evh_2095089031469157_close_8sz4kef9g],
    });
  const inst_evh_2095089031469157_clickPreventStop_h55sdtige =
    new actionTypes.EventHandlerImpl({
      action: act_2095089031469157_showModal_fa1vdg163,
      event: 'click.prevent.stop',
      displayName: 'showModal',
    });
  const inst_evh_2095089031469157_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2095089031469157_clickPreventStop_h55sdtige],
      event: 'click.prevent.stop',
    });
  const act_294572930540096_emit_0sb9uvzxx = new actionTypes.EmitAction({
    actionArgs: act_294572930540096_emit_0sb9uvzxx_getActionArgs,
    displayName: 'emitReload',
  });
  const inst_evh_294572930540096_close_sq5j61vab =
    new actionTypes.EventHandlerImpl({
      action: act_294572930540096_emit_0sb9uvzxx,
      event: 'close',
      when: inst_evh_294572930540096_close_sq5j61vab_getWhen,
      displayName: 'emitReload',
    });
  const act_294572930540096_showModal_kk2l5thy1 =
    new actionTypes.ShowModalAction({
      actionArgs: act_294572930540096_showModal_kk2l5thy1_getActionArgs,
      events: [inst_evh_294572930540096_close_sq5j61vab],
    });
  const inst_evh_294572930540096_clickPreventStop_shcmnabpe =
    new actionTypes.EventHandlerImpl({
      action: act_294572930540096_showModal_kk2l5thy1,
      event: 'click.prevent.stop',
      displayName: 'showModal',
    });
  const inst_evh_294572930540096_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_294572930540096_clickPreventStop_shcmnabpe],
      event: 'click.prevent.stop',
    });
  const act_6027561141562583_emit_4oncvilsr = new actionTypes.EmitAction({
    actionArgs: act_6027561141562583_emit_4oncvilsr_getActionArgs,
    displayName: 'emitReload',
  });
  const inst_evh_6027561141562583_reload_x8gujglhr =
    new actionTypes.EventHandlerImpl({
      action: act_6027561141562583_emit_4oncvilsr,
      event: 'reload',
      displayName: 'emitReload',
    });
  const inst_evh_6027561141562583_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_6027561141562583_reload_x8gujglhr],
    event: 'reload',
  });

  const actions = {
    inst_evh_7315092382398562_reload_meibz6qtv,
    inst_evh_7315092382398562_reload,
    inst_evh_2095089031469157_close_8sz4kef9g,
    emitReload: inst_evh_2095089031469157_close_8sz4kef9g,
    inst_evh_2095089031469157_clickPreventStop_h55sdtige,
    inst_evh_2095089031469157_clickPreventStop,
    inst_evh_294572930540096_close_sq5j61vab,
    inst_evh_294572930540096_clickPreventStop_shcmnabpe,
    inst_evh_294572930540096_clickPreventStop,
    inst_evh_6027561141562583_reload_x8gujglhr,
    inst_evh_6027561141562583_reload,
  };

  const evh_7315092382398562_reload_meibz6qtv = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_meibz6qtv.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2095089031469157_close_8sz4kef9g = async (event, scope) => {
    return await actions.inst_evh_2095089031469157_close_8sz4kef9g.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2095089031469157_clickPreventStop_h55sdtige = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2095089031469157_clickPreventStop_h55sdtige.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2095089031469157_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2095089031469157_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_294572930540096_close_sq5j61vab = async (event, scope) => {
    return await actions.inst_evh_294572930540096_close_sq5j61vab.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_294572930540096_clickPreventStop_shcmnabpe = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_294572930540096_clickPreventStop_shcmnabpe.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_294572930540096_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_294572930540096_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6027561141562583_reload_x8gujglhr = async (event, scope) => {
    return await actions.inst_evh_6027561141562583_reload_x8gujglhr.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6027561141562583_reload = async (event, scope) => {
    return await actions.inst_evh_6027561141562583_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpUsercontrol
    :loading="loading"
    :reloading="reloading"
    :loaded="loaded"
    :error="error"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <ItpWidgetCard>
      <template #header>
        <ItpBox alignItems="center">
          <strong>
            {{ pgettext('Contract', 'Services and Fees') }}
          </strong>
          <ItpButton
            :text="gettext('Add service...')"
            variant="light"
            size="sm"
            class="ms-auto"
            v-if="!readonly"
            @click.prevent.stop="
              evh_2095089031469157_clickPreventStop($event, {})
            "
          >
          </ItpButton>
        </ItpBox>
      </template>
      <BListGroup>
        <BListGroupItem v-if="!contract.services || !contract.services.length">
          <ItpText>
            {{ pgettext('Contract', 'No services have been added yet.') }}
          </ItpText>
        </BListGroupItem>
        <BListGroupItem
          :key="service.id"
          v-for="service in filterItems(contract.services)"
        >
          <ItpBox alignItems="center" :gap="2">
            <UcContractServiceCard :item="service" class="flex-fill">
            </UcContractServiceCard>
            <BDropdown
              size="sm"
              toggle-class="btn-default-borderless"
              no-caret
              variant="none"
              class="ms-auto mb-auto"
            >
              <template #button-content>
                <ItpIcon fa="ellipsis-v" class="mx-1"> </ItpIcon>
              </template>
              <BDropdownItemButton
                @click.prevent.stop="
                  evh_294572930540096_clickPreventStop($event, { service })
                "
              >
                <ItpText :text="pgettext('Command', 'Edit')"> </ItpText>
              </BDropdownItemButton>
            </BDropdown>
          </ItpBox>
          <hr />
          <UcContractServiceFees
            :contract="contract"
            :service="service"
            :reload="lastLoaded"
            :readonly="readonly"
            @reload="evh_6027561141562583_reload($event, { service })"
          >
          </UcContractServiceFees>
        </BListGroupItem>
      </BListGroup>
    </ItpWidgetCard>
  </ItpUsercontrol>
</template>
